const SQRT3 = Math.sqrt(3);
const SQRT12 = Math.sqrt(12);

export const VALVE_PRESSURE_UNITS = [
  { key: 'bar', label: 'bar', toBar: 1 },
  { key: 'kgf/cm2', label: 'kgf/cm²', toBar: 0.980665 },
  { key: 'psi', label: 'psi', toBar: 0.0689475729 },
  { key: 'kPa', label: 'kPa', toBar: 0.01 },
  { key: 'MPa', label: 'MPa', toBar: 10 },
  { key: 'mmHg', label: 'mmHg', toBar: 0.00133322368 },
  { key: 'mmH2O', label: 'mmH₂O', toBar: 0.0000980665 },
];

export const DEFAULT_SAFETY_VALVE_DATA = {
  profile_mode: 'pressure',
  method: 'Determinación de presión de apertura y cierre mediante aplicación progresiva de presión con patrón certificado.',
  procedure: 'Planillas históricas P-DT-PT-11-02 · Calibración de válvulas de seguridad de presión PSV.',
  pressure_unit: 'bar',
  set_pressure: null,
  target_pressure_bar: null,
  operating_pressure: null,
  initial_pressure: null,
  final_pressure: null,
  working_temperature_c: null,
  service_fluid: '',
  inlet_size: '',
  outlet_size: '',
  connection_type: '',
  repeat_count: 3,
  require_closing: true,
  opening_tolerance_percent: null,
  // Regla DUAL de ASME Sec. VIII UG-134(d)(1) (hoy ASME XIII): la tolerancia de
  // la presion de timbre es ABSOLUTA (+/-15 kPa) hasta 500 kPa y PORCENTUAL
  // (+/-3 %) por encima. Equivale a +/-2 psi hasta 70 psi; se usan los valores SI
  // tal como los imprime el codigo. Con solo porcentaje, una PSV de baja presion
  // se exigia al doble de lo que pide la norma (QA 2026-09-06).
  opening_tolerance_mode: 'asme_dual', // 'asme_dual' | 'percent'
  asme_threshold_kpa: 500,
  asme_absolute_tolerance_kpa: 15,
  asme_percent_tolerance: 3,
  max_blowdown_percent: null,
  evaluation_mode: 'report_only',
  decision_rule: 'simple',
  leak_test_pressure: null,
  leak_test_result: 'not_performed',
  inspection: {
    label_status: 'not_checked',
    seal_status: 'not_checked',
    external_status: 'not_checked',
    internal_status: 'not_checked',
  },
  procedures: {
    cleaning: false,
    seating: false,
    painting: false,
    tests: true,
    recalibration: false,
    sealing: false,
    photographs: false,
  },
  uncertainty_budget: { other_percent: 0 },
  environment_readings: [
    { key: 'start', label: 'Inicio', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'middle', label: 'Durante', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'end', label: 'Fin', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
  standard_configuration: {},
  result_summary: null,
};

export function finiteOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const normalized = typeof value === 'string' ? value.trim().replace(',', '.') : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

export function pressureUnitDefinition(unit) {
  const text = String(unit || '').trim().toLowerCase().replace('²', '2');
  return VALVE_PRESSURE_UNITS.find((item) => item.key.toLowerCase() === text || item.label.toLowerCase().replace('²', '2') === text)
    || VALVE_PRESSURE_UNITS[0];
}

export function pressureToBar(value, unit = 'bar', profileMode = 'pressure') {
  const number = finiteOrNull(value);
  if (number === null) return null;
  const magnitude = number * pressureUnitDefinition(unit).toBar;
  return profileMode === 'vacuum' ? -Math.abs(magnitude) : magnitude;
}

export function pressureFromBar(value, unit = 'bar', profileMode = 'pressure') {
  const number = finiteOrNull(value);
  if (number === null) return null;
  const magnitude = number / pressureUnitDefinition(unit).toBar;
  return profileMode === 'vacuum' ? Math.abs(magnitude) : magnitude;
}

export function inferSafetyValveProfile(instrument = {}, templateCode = '') {
  const text = [instrument.type, instrument.model, instrument.unit, templateCode].filter(Boolean).join(' ').toLowerCase();
  const vacuum = text.includes('vac') || text.includes('mmhg') || text.includes('mmh2o');
  return {
    code: vacuum ? 'VALVE:safety_vacuum:v1' : 'VALVE:safety_pressure:v1',
    revision: 'v1',
    title: vacuum ? 'Válvula de seguridad para vacío' : 'Válvula de seguridad a presión',
    profileMode: vacuum ? 'vacuum' : 'pressure',
    unit: vacuum ? (text.includes('mmhg') ? 'mmHg' : 'mmH2O') : (pressureUnitDefinition(instrument.unit).key || 'bar'),
    requireClosing: !vacuum,
  };
}

/**
 * Tolerancia de apertura efectiva. Devuelve siempre un porcentaje (para que la
 * banda de guarda y la comparacion sigan siendo homogeneas) y, cuando aplica la
 * regla de ASME, tambien el valor absoluto equivalente en la unidad de trabajo.
 */
export function resolveOpeningTolerance(familyData = {}, setPressure = null) {
  const declaredPercent = Math.abs(finiteOrNull(familyData.opening_tolerance_percent) ?? 0);
  const mode = familyData.opening_tolerance_mode === 'asme_dual' ? 'asme_dual' : 'percent';
  const sp = finiteOrNull(setPressure);
  if (mode !== 'asme_dual' || familyData.profile_mode === 'vacuum' || sp === null || sp === 0) {
    return { percent: declaredPercent, rule: 'percent', absolute: null };
  }
  const factor = pressureUnitDefinition(familyData.pressure_unit).toBar;
  const setBar = Math.abs(sp * factor);
  if (!(setBar > 0)) return { percent: declaredPercent, rule: 'percent', absolute: null };
  const kpaToBar = 0.01;
  const thresholdBar = Math.abs(finiteOrNull(familyData.asme_threshold_kpa) ?? 500) * kpaToBar;
  const absToleranceBar = Math.abs(finiteOrNull(familyData.asme_absolute_tolerance_kpa) ?? 15) * kpaToBar;
  const asmePercent = Math.abs(finiteOrNull(familyData.asme_percent_tolerance) ?? 3);
  if (setBar <= thresholdBar) {
    return {
      percent: (absToleranceBar / setBar) * 100,
      rule: 'asme_absolute',
      absolute: absToleranceBar / factor,
    };
  }
  return {
    percent: asmePercent,
    rule: 'asme_percent',
    absolute: (setBar * asmePercent / 100) / factor,
  };
}

export function mergeSafetyValveData(value = {}) {
  const input = value && typeof value === 'object' ? value : {};
  const environment = Array.isArray(input.environment_readings) ? input.environment_readings : DEFAULT_SAFETY_VALVE_DATA.environment_readings;
  const profileMode = input.profile_mode === 'vacuum' ? 'vacuum' : 'pressure';
  const pressureUnit = pressureUnitDefinition(input.pressure_unit).key;
  const setPressure = finiteOrNull(input.set_pressure);
  return {
    ...DEFAULT_SAFETY_VALVE_DATA,
    ...input,
    profile_mode: profileMode,
    pressure_unit: pressureUnit,
    set_pressure: setPressure,
    target_pressure_bar: pressureToBar(setPressure, pressureUnit, profileMode),
    repeat_count: Math.min(5, Math.max(1, Number(input.repeat_count) || 3)),
    require_closing: input.require_closing ?? profileMode !== 'vacuum',
    // La regla dual es para alivio de PRESION. En vacio el piso absoluto puede
    // superar varias veces la presion de timbre, asi que ahi siempre porcentaje.
    opening_tolerance_mode: profileMode === 'vacuum'
      ? 'percent'
      : (input.opening_tolerance_mode === 'percent' ? 'percent' : 'asme_dual'),
    inspection: { ...DEFAULT_SAFETY_VALVE_DATA.inspection, ...(input.inspection || {}) },
    procedures: { ...DEFAULT_SAFETY_VALVE_DATA.procedures, ...(input.procedures || {}) },
    uncertainty_budget: { ...DEFAULT_SAFETY_VALVE_DATA.uncertainty_budget, ...(input.uncertainty_budget || {}) },
    environment_readings: DEFAULT_SAFETY_VALVE_DATA.environment_readings.map((fallback, index) => ({
      ...fallback,
      ...(environment[index] || {}),
    })),
    standard_configuration: input.standard_configuration || {},
    result_summary: input.result_summary || null,
  };
}

export function safetyValveBlock() {
  return {
    block_key: 'valve_performance',
    display_name: 'Apertura, cierre y hermeticidad',
    unit: 'varios',
    applicable: true,
    point_count: 5,
    sort_order: 1,
    mode: 'simple',
  };
}

function mean(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  return valid.length ? valid.reduce((sum, value) => sum + value, 0) / valid.length : null;
}

function sampleStandardDeviation(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (valid.length < 2) return 0;
  const average = mean(valid);
  return Math.sqrt(valid.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (valid.length - 1));
}

function standardResults(snapshot) {
  return (snapshot?.results || [])
    .map((result) => ({
      ...result,
      x: finiteOrNull(result.reference_value),
      correction: finiteOrNull(result.correction) ?? -(finiteOrNull(result.error) ?? 0),
      uncertainty: Math.abs(finiteOrNull(result.uncertainty_expanded) ?? 0),
      k: finiteOrNull(result.coverage_factor) ?? finiteOrNull(snapshot?.certificate?.coverage_factor) ?? 2,
      hysteresis: Math.abs(finiteOrNull(result?.metadata?.hysteresis) ?? 0),
    }))
    .filter((result) => result.x !== null)
    .sort((a, b) => a.x - b.x);
}

function interpolate(a, b, x, field) {
  if (!b || a.x === b.x) return finiteOrNull(a[field]) ?? 0;
  const ratio = (x - a.x) / (b.x - a.x);
  return (finiteOrNull(a[field]) ?? 0) + ratio * ((finiteOrNull(b[field]) ?? 0) - (finiteOrNull(a[field]) ?? 0));
}

export function standardAtPressure(snapshot, pressureBar) {
  const x = finiteOrNull(pressureBar);
  const results = standardResults(snapshot);
  if (x === null || !results.length || x < results[0].x || x > results[results.length - 1].x) return null;
  const upperIndex = results.findIndex((result) => result.x >= x);
  const upper = results[Math.max(0, upperIndex)];
  const lower = results[Math.max(0, upperIndex - 1)] || upper;
  return {
    correction_bar: interpolate(lower, upper, x, 'correction'),
    uncertainty_expanded_bar: interpolate(lower, upper, x, 'uncertainty'),
    coverage_factor: interpolate(lower, upper, x, 'k') || 2,
    hysteresis_bar: interpolate(lower, upper, x, 'hysteresis'),
    range_min_bar: results[0].x,
    range_max_bar: results[results.length - 1].x,
  };
}

function correctedReading(value, familyData, snapshot) {
  const rawBar = pressureToBar(value, familyData.pressure_unit, familyData.profile_mode);
  const standard = standardAtPressure(snapshot, rawBar);
  if (rawBar === null || !standard) return null;
  const correctedBar = rawBar + standard.correction_bar;
  return {
    raw: finiteOrNull(value),
    raw_bar: rawBar,
    correction_bar: standard.correction_bar,
    correction: pressureFromBar(standard.correction_bar, familyData.pressure_unit, 'pressure'),
    corrected_bar: correctedBar,
    corrected: pressureFromBar(correctedBar, familyData.pressure_unit, familyData.profile_mode),
    standard,
  };
}

function uncertaintyForReadings(corrected, familyData) {
  if (!corrected.length) return null;
  const factor = pressureUnitDefinition(familyData.pressure_unit).toBar;
  const values = corrected.map((item) => item.corrected);
  const averageStandardU = mean(corrected.map((item) => item.standard.uncertainty_expanded_bar / item.standard.coverage_factor)) / factor;
  const maxHysteresis = Math.max(...corrected.map((item) => item.standard.hysteresis_bar)) / factor;
  const resolutionBar = Math.abs(finiteOrNull(familyData.standard_configuration?.reference?.capability?.resolution) ?? 0.001);
  const uResolution = (resolutionBar / factor) / SQRT12;
  const repeatability = sampleStandardDeviation(values);
  const uRepeatability = repeatability / Math.sqrt(Math.max(1, values.length));
  const uHysteresis = (maxHysteresis / 2) / SQRT3;
  const averageValue = Math.abs(mean(values) ?? 0);
  const uOther = averageValue * (Math.abs(finiteOrNull(familyData.uncertainty_budget?.other_percent) ?? 0) / 100) / SQRT3;
  const uc = Math.hypot(averageStandardU, uResolution, uRepeatability, uHysteresis, uOther);
  return {
    mean: mean(values),
    repeatability,
    u_standard: averageStandardU,
    u_resolution: uResolution,
    u_repeatability: uRepeatability,
    u_hysteresis: uHysteresis,
    u_other: uOther,
    uc,
    k: 2,
    u_expanded: uc * 2,
  };
}

export function computeSafetyValveMetrics(rows = [], session = {}, snapshot = null, block = {}) {
  const familyData = mergeSafetyValveData(session.family_data);
  const repeatCount = familyData.repeat_count;
  const activeRows = Array.from({ length: repeatCount }, (_, index) => rows[index] || { trial_no: index + 1 });
  const openings = activeRows.map((row) => correctedReading(row.opening_reading, familyData, snapshot)).filter(Boolean);
  const closings = activeRows.map((row) => correctedReading(row.closing_reading, familyData, snapshot)).filter(Boolean);
  if (!openings.length) return null;
  const opening = uncertaintyForReadings(openings, familyData);
  const closing = closings.length ? uncertaintyForReadings(closings, familyData) : null;
  const setPressure = finiteOrNull(familyData.set_pressure);
  if (!opening || setPressure === null || setPressure <= 0) return null;
  const errorAbs = opening.mean - setPressure;
  const errorPct = (errorAbs / setPressure) * 100;
  const blowdown = closing && opening.mean !== 0 ? ((opening.mean - closing.mean) / opening.mean) * 100 : null;
  const blowdownUc = closing && opening.mean !== 0
    ? 100 * Math.hypot((closing.mean / (opening.mean ** 2)) * opening.uc, closing.uc / opening.mean)
    : null;
  const blowdownU = blowdownUc === null ? null : blowdownUc * 2;
  const toleranceRule = resolveOpeningTolerance(familyData, setPressure);
  const openingTolerance = toleranceRule.percent;
  const maxBlowdown = Math.abs(finiteOrNull(familyData.max_blowdown_percent) ?? 0);
  const guardOpeningPct = familyData.decision_rule === 'guard_band' ? (opening.u_expanded / setPressure) * 100 : 0;
  const guardBlowdownPct = familyData.decision_rule === 'guard_band' ? (blowdownU ?? 0) : 0;
  // Evaluación POR ENSAYO, no por promedio (QA 2026-09-06). Una válvula de
  // seguridad debe abrir dentro de tolerancia en CADA intento: promediar diluye
  // una apertura fuera de rango y puede aprobar una válvula no conforme.
  // Ejemplo real detectado: set 8 bar, tol 3 %, aperturas 7,916 / 8,084 / 8,36.
  // El peor ensayo está a +4,5 % pero el promedio queda en +1,5 % -> aprobaba.
  const openingTrials = openings.map((item, index) => {
    const value = finiteOrNull(item.corrected);
    const trialErrorAbs = value === null ? null : value - setPressure;
    const trialErrorPct = trialErrorAbs === null ? null : (trialErrorAbs / setPressure) * 100;
    const trialPass = familyData.evaluation_mode === 'tolerance' && openingTolerance > 0 && trialErrorPct !== null
      ? Math.abs(trialErrorPct) + guardOpeningPct <= openingTolerance
      : null;
    return {
      trial_no: index + 1,
      opening: value,
      error_abs: trialErrorAbs,
      error_pct: trialErrorPct,
      in_tolerance: trialPass,
    };
  });
  const worstOpeningErrorPct = openingTrials.reduce((worst, trial) => (
    trial.error_pct === null
      ? worst
      : (worst === null || Math.abs(trial.error_pct) > Math.abs(worst) ? trial.error_pct : worst)
  ), null);
  const openingTrialVerdicts = openingTrials
    .map((trial) => trial.in_tolerance)
    .filter((value) => value !== null);
  const openingPass = familyData.evaluation_mode === 'tolerance' && openingTolerance > 0
    ? (openingTrialVerdicts.length ? openingTrialVerdicts.every(Boolean) : null)
    : null;
  const blowdownPass = familyData.evaluation_mode === 'tolerance' && blowdown !== null && maxBlowdown > 0
    ? blowdown >= 0 && blowdown + guardBlowdownPct <= maxBlowdown
    : null;
  const leakPass = familyData.leak_test_result === 'satisfactory'
    ? true
    : familyData.leak_test_result === 'fail' ? false : null;
  const evaluated = [openingPass, blowdownPass, leakPass].filter((value) => value !== null);
  const overallPass = familyData.evaluation_mode === 'tolerance' && evaluated.length ? evaluated.every(Boolean) : null;
  return {
    block_id: block.id || null,
    unit: familyData.pressure_unit,
    profile_mode: familyData.profile_mode,
    set_pressure: setPressure,
    opening,
    closing,
    opening_readings: openings,
    closing_readings: closings,
    error_abs: errorAbs,
    error_pct: errorPct,
    opening_trials: openingTrials,
    worst_opening_error_pct: worstOpeningErrorPct,
    blowdown_percent: blowdown,
    blowdown_u_expanded_percent: blowdownU,
    opening_tolerance_percent: openingTolerance || null,
    opening_tolerance_rule: toleranceRule.rule,
    opening_tolerance_abs: toleranceRule.absolute,
    max_blowdown_percent: maxBlowdown || null,
    opening_pass: openingPass,
    blowdown_pass: blowdownPass,
    leak_pass: leakPass,
    overall_pass: overallPass,
    evaluation_mode: familyData.evaluation_mode,
    decision_rule: familyData.decision_rule,
  };
}

export function validateSafetyValveRows(rows = [], familyData = {}, snapshot = null) {
  const merged = mergeSafetyValveData(familyData);
  const issues = [];
  if (finiteOrNull(merged.set_pressure) === null || finiteOrNull(merged.set_pressure) <= 0) issues.push('Falta una presión de timbre mayor que cero.');
  if (!snapshot) issues.push('Falta seleccionar un patrón de presión vigente.');
  for (let index = 0; index < merged.repeat_count; index += 1) {
    const row = rows[index] || {};
    const opening = finiteOrNull(row.opening_reading);
    const closing = finiteOrNull(row.closing_reading);
    if (opening === null) issues.push(`Falta la apertura del ensayo ${index + 1}.`);
    else if (snapshot && !standardAtPressure(snapshot, pressureToBar(opening, merged.pressure_unit, merged.profile_mode))) {
      issues.push(`La apertura del ensayo ${index + 1} está fuera del alcance certificado del patrón.`);
    }
    if (merged.require_closing && closing === null) issues.push(`Falta el cierre del ensayo ${index + 1}.`);
    else if (closing !== null && snapshot && !standardAtPressure(snapshot, pressureToBar(closing, merged.pressure_unit, merged.profile_mode))) {
      issues.push(`El cierre del ensayo ${index + 1} está fuera del alcance certificado del patrón.`);
    }
  }
  if (merged.leak_test_result === 'not_performed') issues.push('Falta registrar el resultado de hermeticidad o marcarlo como no aplicable.');
  if (merged.evaluation_mode === 'tolerance') {
    if (!(finiteOrNull(merged.opening_tolerance_percent) > 0)) issues.push('Falta la tolerancia de apertura.');
    if (merged.require_closing && !(finiteOrNull(merged.max_blowdown_percent) > 0)) issues.push('Falta el blowdown máximo permitido.');
  }
  const target = pressureToBar(merged.set_pressure, merged.pressure_unit, merged.profile_mode);
  if (snapshot && !standardAtPressure(snapshot, target)) issues.push('La presión de timbre está fuera del alcance certificado del patrón.');
  return issues;
}
